import clsx from 'clsx';
import { Check, Package } from 'lucide-react';

import {
  AddonHealthStatus,
  AddonLifecycleStatus,
  AddonsAddonListItem,
} from '@api/types.gen';

import { useAddonSwitcher } from '@/react/portainer/addons/queries/useAddonSwitcher';
import { useCurrentUser } from '@/react/hooks/useUser';
import { AddonStatusBadge } from '@/react/portainer/addons/AddonStatusBadge';
import portainerIcon from '@/assets/addons/portainer-ops.svg';

import { Link } from '@@/Link';
import { FallbackImage } from '@@/FallbackImage';

export function AddonSwitcher() {
  const addonsQuery = useAddonSwitcher();
  const {
    user: { PortainerAccessDenied: portainerAccessDenied },
  } = useCurrentUser();
  const containerStyles = clsx(
    'w-[400px] rounded-lg',
    'bg-graphite-800 shadow-lg',
    'border border-solid border-graphite-600',
    'th-highcontrast:border-white th-highcontrast:bg-black'
  );
  const headerStyles = clsx(
    'px-3 py-2 text-[10px] font-semibold uppercase tracking-widest',
    'text-gray-4 th-highcontrast:text-white'
  );

  if (addonsQuery.isError) {
    return (
      <div className={containerStyles}>
        <div className={headerStyles}>Products</div>
        <div className="p-5 text-center text-white">
          An error occurred loading Addons
        </div>
      </div>
    );
  }

  const allAddons = addonsQuery.data?.addons || [];
  const enabledAddons = allAddons.filter((addon) => addon.enabled) || [];
  const hasUnhealthyAddons = enabledAddons.some(
    (addon) => addon.healthStatus === AddonHealthStatus.ADDON_HEALTH_UNHEALTHY
  );

  if (!allAddons.length) {
    return (
      <div className={containerStyles}>
        <div className={headerStyles}>Products</div>
        {!portainerAccessDenied && (
          <div className="px-2">
            <PortainerOpsLink />
          </div>
        )}
        {addonsQuery.isLoading ? (
          <div className="p-5 text-center text-white">Loading&hellip;</div>
        ) : (
          <NoAddonsInstalled />
        )}
      </div>
    );
  }

  return (
    <div className={containerStyles}>
      <div className={headerStyles}>Products</div>
      <ul className="list-none !pb-2 pl-0">
        {!portainerAccessDenied && (
          <li key="portainer-ops" className="px-2">
            <PortainerOpsLink />
          </li>
        )}
        {enabledAddons.map((product) => {
          const isUnhealthy =
            product.healthStatus === AddonHealthStatus.ADDON_HEALTH_UNHEALTHY;

          return (
            <li key={product.id} className="px-2">
              <a
                href={product.path}
                aria-disabled={isUnhealthy}
                tabIndex={isUnhealthy ? -1 : undefined}
                className={clsx(
                  'flex w-full items-center gap-3 px-3 py-2',
                  'rounded-md border-0 bg-transparent text-left',
                  isUnhealthy
                    ? 'pointer-events-none cursor-not-allowed opacity-50'
                    : 'hover:bg-graphite-700 hover:no-underline th-highcontrast:hover:bg-gray-11'
                )}
              >
                <FallbackImage
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm"
                  src={product.icon}
                  alt={`${product.displayName} logo`}
                  fallbackIcon={<Package className="text-2xl" />}
                />
                <span className="flex-1 overflow-hidden">
                  <span className="block truncate text-sm font-medium text-white">
                    {product.displayName}
                  </span>
                  <span className="block truncate text-xs text-gray-4 th-highcontrast:text-gray-warm-5">
                    {product.shortDescription}
                  </span>
                </span>
                {!isInstalledAndHealthy(product) && (
                  <AddonStatusBadge
                    lifecycleStatus={product.lifecycleStatus}
                    lifecycleMessage={product.lifecycleStatusMessage}
                    healthStatus={product.healthStatus}
                    healthMessage={product.healthMessage}
                  />
                )}
              </a>
            </li>
          );
        })}
        {hasUnhealthyAddons && (
          <li key="unhealthy">
            <AddonsUnhealthy />
          </li>
        )}
      </ul>
    </div>
  );
}

// The status badge is redundant for a cleanly running addon, so it's hidden
// whenever the addon is installed with no upgrade issue and reporting healthy.
function isInstalledAndHealthy(product: AddonsAddonListItem) {
  return (
    product.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_INSTALLED &&
    !product.lifecycleStatusMessage &&
    product.healthStatus === AddonHealthStatus.ADDON_HEALTH_HEALTHY
  );
}

function AddonsUnhealthy() {
  const { isPureAdmin } = useCurrentUser();

  return (
    <div className="text-center text-sm text-gray-4 th-highcontrast:text-white">
      <p className="mb-1 font-medium text-white">One more add-ons unhealthy</p>
      <p className="mb-0">
        {!isPureAdmin && 'Ask a Portainer admin about troubleshooting add-ons.'}
      </p>
      {isPureAdmin && (
        <Link
          to="portainer.addons"
          data-cy="addon-switcher-browse-link"
          className="text-blue-7 hover:underline th-highcontrast:text-blue-4"
        >
          Manage add-ons
        </Link>
      )}
    </div>
  );
}

function NoAddonsInstalled() {
  const { isPureAdmin } = useCurrentUser();

  return (
    <div className="px-5 py-4 text-center text-sm text-gray-4 th-highcontrast:text-white">
      <p className="mb-1 font-medium text-white">No add-ons installed</p>
      <p className="mb-2">
        Add-ons extend Portainer with self-service portals and other tools.
        {!isPureAdmin && ' Ask a Portainer admin about installing add-ons.'}
      </p>
      {isPureAdmin && (
        <Link
          to="portainer.addons"
          data-cy="addon-switcher-browse-link"
          className="text-blue-7 hover:underline th-highcontrast:text-blue-4"
        >
          Browse available add-ons
        </Link>
      )}
    </div>
  );
}

function PortainerOpsLink() {
  return (
    <Link
      to="portainer.home"
      data-cy="portainer-addons-ops-link"
      className={clsx(
        'flex w-full items-center gap-3 px-3 py-2',
        'rounded-md border-0 bg-transparent text-left',
        'hover:bg-graphite-700 hover:no-underline th-highcontrast:hover:bg-gray-11'
      )}
    >
      <FallbackImage
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm"
        src={portainerIcon}
        alt="Portainer Ops logo"
        fallbackIcon={<Package className="text-2xl" />}
      />
      <span className="flex-1 overflow-hidden">
        <span className="block truncate text-sm font-medium text-white">
          Portainer Ops
        </span>
        <span className="block truncate text-xs text-gray-4 th-highcontrast:text-gray-warm-5">
          Infrastructure and cluster management
        </span>
      </span>
      <Check className="stroke-blue-7 th-highcontrast:stroke-blue-4" />
    </Link>
  );
}
